import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { releaseApi } from '../api'
import type { ExternalSecretStatus, ArgoCdApp, MigrationStatusResponse } from '../api'
import { PageDescription } from '../components/PageDescription'

// ---- Migrations panel ----

function MigrationsPanel({ mig, visible }: { mig: MigrationStatusResponse; visible: boolean }) {
    const [showAll, setShowAll] = useState(false)
    const hasPending = mig.pending.length > 0
    const displayApplied = showAll ? mig.applied : mig.applied.slice(-5)

    return (
        <section
            className="rounded-xl border border-slate-700 bg-slate-800/40 overflow-hidden transition-all duration-500"
            style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(16px)' }}
        >
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700/50 bg-slate-800/60">
                <div className="flex items-center gap-3">
                    <svg className="h-4 w-4 text-slate-400" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M2 4h12M2 8h8M2 12h5" strokeLinecap="round" />
                    </svg>
                    <span className="text-sm font-semibold text-white">EF Migrations</span>
                    <span className="text-[10px] text-slate-500">{mig.applied.length} applied</span>
                </div>
                {hasPending ? (
                    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-amber-500/15 text-amber-400 ring-1 ring-amber-500/30">
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                        {mig.pending.length} pending
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                        Up to date
                    </span>
                )}
            </div>

            {hasPending && (
                <div className="border-b border-slate-700/30">
                    <div className="px-5 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-amber-500/80 bg-amber-950/20">
                        Pending
                    </div>
                    {mig.pending.map(m => (
                        <div key={m} className="flex items-center gap-3 px-5 py-2 bg-amber-950/10">
                            <span className="h-1.5 w-1.5 rounded-full bg-amber-400 shrink-0" />
                            <span className="text-xs font-mono text-amber-300">{m}</span>
                        </div>
                    ))}
                </div>
            )}

            <div>
                <div className="px-5 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-500 bg-slate-800/20">
                    Applied {!showAll && mig.applied.length > 5 ? `(last 5 of ${mig.applied.length})` : `(${mig.applied.length})`}
                </div>
                {displayApplied.map(m => (
                    <div key={m} className="flex items-center gap-3 px-5 py-2 border-b border-slate-700/20 last:border-0">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500/60 shrink-0" />
                        <span className="text-xs font-mono text-slate-400">{m}</span>
                    </div>
                ))}
                {mig.applied.length > 5 && (
                    <button
                        onClick={() => setShowAll(v => !v)}
                        className="w-full px-5 py-2 text-xs text-slate-500 hover:text-slate-300 transition-colors text-left"
                    >
                        {showAll ? '▲ Show less' : `▼ Show all ${mig.applied.length} migrations`}
                    </button>
                )}
            </div>
        </section>
    )
}

// ---- ExternalSecret panel ----

function esoStatusStyle(s: ExternalSecretStatus) {
    if (s.ready) return { dot: 'bg-emerald-400', badge: 'bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30', label: s.reason ?? 'Synced' }
    return { dot: 'bg-red-500', badge: 'bg-red-500/15 text-red-400 ring-1 ring-red-500/30', label: s.reason ?? 'Error' }
}

function ExternalSecretsPanel({ secrets, visible }: { secrets: ExternalSecretStatus[]; visible: boolean }) {
    const [expanded, setExpanded] = useState<string | null>(null)

    const grouped = secrets.reduce<Record<string, ExternalSecretStatus[]>>((acc, s) => {
        ;(acc[s.namespace] ??= []).push(s)
        return acc
    }, {})

    const errorCount  = secrets.filter(s => !s.ready).length
    const totalCount  = secrets.length

    return (
        <section
            className="rounded-xl border border-slate-700 bg-slate-800/40 overflow-hidden transition-all duration-500"
            style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(16px)' }}
        >
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700/50 bg-slate-800/60">
                <div className="flex items-center gap-3">
                    <svg className="h-4 w-4 text-slate-400" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="2" y="5" width="12" height="8" rx="1.5" />
                        <path d="M5 5V4a3 3 0 0 1 6 0v1" strokeLinecap="round" />
                    </svg>
                    <span className="text-sm font-semibold text-white">External Secrets</span>
                    <span className="text-[10px] text-slate-500">{totalCount} secret{totalCount !== 1 ? 's' : ''}</span>
                </div>
                {errorCount > 0 ? (
                    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-red-500/15 text-red-400 ring-1 ring-red-500/30">
                        <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                        {errorCount} error{errorCount !== 1 ? 's' : ''}
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                        All synced
                    </span>
                )}
            </div>

            {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([ns, nsSecrets]) => (
                <div key={ns} className="border-b border-slate-700/30 last:border-0">
                    <div className="px-5 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-500 bg-slate-800/20">
                        {ns}
                    </div>
                    {nsSecrets.map(s => {
                        const style = esoStatusStyle(s)
                        const key = `${s.namespace}/${s.name}`
                        const isOpen = expanded === key

                        return (
                            <div key={s.name}>
                                <button
                                    onClick={() => setExpanded(isOpen ? null : key)}
                                    className="w-full flex items-center gap-3 px-5 py-2 hover:bg-slate-800/40 transition-colors text-left"
                                >
                                    <span className={`h-2 w-2 rounded-full shrink-0 ${style.dot}`} />
                                    <span className="text-xs font-mono text-slate-300 flex-1">{s.name}</span>
                                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${style.badge}`}>
                                        {style.label}
                                    </span>
                                    {s.message && (
                                        <svg className={`h-3 w-3 text-slate-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
                                            <path d="M2 4l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    )}
                                </button>
                                {isOpen && s.message && (
                                    <div className="mx-5 mb-2 px-3 py-2 rounded-lg bg-red-950/50 border border-red-800/50 text-xs font-mono text-red-300 whitespace-pre-wrap break-all">
                                        {s.message}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            ))}

            {secrets.length === 0 && (
                <p className="px-5 py-8 text-sm text-slate-500 text-center">No ExternalSecrets found.</p>
            )}
        </section>
    )
}

// ---- ArgoCD errors panel ----

function relativeTime(iso: string | null): string {
    if (!iso) return '—'
    const diff = Date.now() - new Date(iso).getTime()
    const mins = Math.floor(diff / 60_000)
    if (mins < 1)  return 'just now'
    if (mins < 60) return `${mins}m ago`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24)  return `${hrs}h ago`
    return `${Math.floor(hrs / 24)}d ago`
}

function ArgoCdAppsPanel({ apps, visible }: { apps: ArgoCdApp[]; visible: boolean }) {
    const unhealthy = apps.filter(a =>
        a.syncStatus !== 'Synced' || a.healthStatus === 'Degraded' || a.healthStatus === 'Missing' || !!a.lastSyncError
    )
    const [expanded, setExpanded] = useState<string | null>(null)

    return (
        <section
            className="rounded-xl border border-slate-700 bg-slate-800/40 overflow-hidden transition-all duration-500"
            style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(16px)', transitionDelay: '80ms' }}
        >
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700/50 bg-slate-800/60">
                <div className="flex items-center gap-3">
                    <svg className="h-4 w-4 text-slate-400" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <circle cx="4" cy="4" r="2" />
                        <circle cx="12" cy="4" r="2" />
                        <circle cx="8" cy="12" r="2" />
                        <path d="M4 6v1.5a2.5 2.5 0 0 0 2.5 2.5h3A2.5 2.5 0 0 0 12 7.5V6" strokeLinecap="round" />
                        <line x1="8" y1="9" x2="8" y2="10" strokeLinecap="round" />
                    </svg>
                    <span className="text-sm font-semibold text-white">ArgoCD Applications</span>
                    <span className="text-[10px] text-slate-500">{apps.length} apps</span>
                </div>
                {unhealthy.length > 0 ? (
                    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-amber-500/15 text-amber-400 ring-1 ring-amber-500/30">
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                        {unhealthy.length} issue{unhealthy.length !== 1 ? 's' : ''}
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                        All healthy
                    </span>
                )}
            </div>

            <div className="divide-y divide-slate-700/30">
                {apps.map(app => {
                    const isOutOfSync = app.syncStatus !== 'Synced'
                    const isDegraded  = app.healthStatus === 'Degraded' || app.healthStatus === 'Missing'
                    const hasError    = !!app.lastSyncError
                    const isOpen      = expanded === app.name

                    const rowStyle = isDegraded || hasError
                        ? 'bg-red-950/10 hover:bg-red-950/20'
                        : isOutOfSync
                            ? 'bg-amber-950/10 hover:bg-amber-950/20'
                            : 'hover:bg-slate-800/30'

                    const statusDot = isDegraded || hasError
                        ? 'bg-red-500'
                        : isOutOfSync
                            ? 'bg-amber-400'
                            : 'bg-emerald-400'

                    return (
                        <div key={app.name}>
                            <button
                                onClick={() => hasError ? setExpanded(isOpen ? null : app.name) : undefined}
                                className={`w-full flex items-center gap-4 px-5 py-3 transition-colors text-left ${rowStyle} ${hasError ? 'cursor-pointer' : 'cursor-default'}`}
                            >
                                <span className={`h-2 w-2 rounded-full shrink-0 ${statusDot}`} />
                                <span className="text-sm font-mono text-slate-200 w-48 shrink-0 truncate">{app.name}</span>
                                <span className="text-xs text-slate-500 w-20 shrink-0">{app.destinationNamespace}</span>
                                <span className={`text-xs shrink-0 ${isOutOfSync ? 'text-amber-400' : 'text-emerald-400'}`}>{app.syncStatus}</span>
                                <span className={`text-xs shrink-0 ${isDegraded ? 'text-red-400' : 'text-slate-400'}`}>{app.healthStatus}</span>
                                <span className="text-xs text-slate-600 ml-auto shrink-0">{relativeTime(app.lastSyncTime)}</span>
                                {hasError && (
                                    <svg className={`h-3 w-3 text-red-400 transition-transform shrink-0 ${isOpen ? 'rotate-180' : ''}`} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <path d="M2 4l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                )}
                            </button>
                            {isOpen && hasError && (
                                <div className="mx-5 mb-3 px-3 py-2 rounded-lg bg-red-950/50 border border-red-800/50 text-xs font-mono text-red-300 whitespace-pre-wrap break-all">
                                    {app.lastSyncError}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>

            {apps.length === 0 && (
                <p className="px-5 py-8 text-sm text-slate-500 text-center">No ArgoCD apps found.</p>
            )}
        </section>
    )
}

// ---- Main page ----

export function TroubleshootPage() {
    const [visible, setVisible] = useState(false)

    const { data, isLoading, isError, error, dataUpdatedAt, refetch, isFetching } = useQuery({
        queryKey: ['cluster-health'],
        queryFn: releaseApi.getClusterHealth,
        refetchInterval: 60_000,
    })

    const { data: migData } = useQuery({
        queryKey: ['migrations'],
        queryFn: releaseApi.getMigrations,
        refetchInterval: 120_000,
    })

    useEffect(() => {
        if (data) {
            const t = setTimeout(() => setVisible(true), 80)
            return () => clearTimeout(t)
        }
        return undefined
    }, [data])

    return (
        <div className="flex flex-col min-h-0 flex-1">
            <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-10">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
                    <div>
                        <span className="text-sm font-semibold text-slate-200">Troubleshoot</span>
                        <PageDescription subtitle="Migrations · ExternalSecrets · ArgoCD sync errors" storageKey="desc-troubleshoot" detail="Surfaces the three most common categories of deployment problems: pending or failed EF migrations, ExternalSecret sync failures (secrets not reaching the cluster), and ArgoCD applications that are stuck OutOfSync." />
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                        {dataUpdatedAt > 0 && (
                            <span>Updated {new Date(dataUpdatedAt).toLocaleTimeString()}</span>
                        )}
                        <button
                            onClick={() => void refetch()}
                            disabled={isFetching}
                            className="rounded-lg border border-slate-700 px-3 py-1.5 text-slate-300 hover:bg-slate-800 hover:text-white transition-colors disabled:opacity-40"
                        >
                            {isFetching ? 'Refreshing…' : 'Refresh'}
                        </button>
                    </div>
                </div>
            </header>

            <main className="flex-1 overflow-y-auto px-4 sm:px-6 py-8">
                {isLoading && (
                    <div className="flex items-center justify-center h-64">
                        <div className="text-slate-500 text-sm animate-pulse">Fetching health data…</div>
                    </div>
                )}

                {isError && (
                    <div className="rounded-xl border border-red-800 bg-red-950/50 p-6 text-red-300 text-sm max-w-2xl space-y-1">
                        <p className="font-medium">Failed to load health data</p>
                        {error instanceof Error && (
                            <p className="font-mono text-xs text-red-400 break-all">{error.message}</p>
                        )}
                    </div>
                )}

                {(data || migData) && (
                    <div className="max-w-5xl mx-auto space-y-6">
                        {migData && <MigrationsPanel mig={migData} visible={visible} />}
                        {data && <ExternalSecretsPanel secrets={data.externalSecrets} visible={visible} />}
                        {data && <ArgoCdAppsPanel apps={data.argoCdApps} visible={visible} />}
                    </div>
                )}
            </main>
        </div>
    )
}
